/* eslint-disable react/no-unescaped-entities */
import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { MANDATORY_PROMPTS, OPTIONAL_PROMPTS } from "@/constants/prompts";
import { Heart, Search as SearchIcon, Star, Inbox as InboxIcon, HelpCircle, User, LogOut, Flag, Shield, X, Upload } from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_908682e2-cfa4-4698-8b7e-10e737f43eaf/artifacts/y2fvty9k_20250404_015718.jpg";

const REPORT_REASONS = ["Catfishing / fake profile", "Misrepresenting orientation or identity", "Harassment or hate speech", "Soliciting money or citizenship benefits", "Sexual / inappropriate content", "Underage user", "Other"];

const frameClass = (tier) => tier === "diamond" ? "frame-diamond" : tier === "platinum" ? "frame-platinum" : "frame-gold";

function TopNav({ active, setActive }) {
  const { user, logout } = useAuth();
  const tabs = [
    { id: "search", label: "Search", icon: <SearchIcon size={16} /> },
    { id: "profile", label: "My Profile", icon: <User size={16} /> },
    { id: "favorites", label: "Favorites", icon: <Star size={16} /> },
    { id: "inbox", label: "Inbox", icon: <InboxIcon size={16} /> },
    { id: "help", label: "Help", icon: <HelpCircle size={16} /> },
  ];
  return (
    <header className="sticky top-0 z-40 glass border-b border-white/10" data-testid="dashboard-nav">
      <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
        <Link to="/app" className="flex items-center gap-3"><img src={LOGO} className="h-9 w-9 rounded-lg object-cover" alt="" /><span className="font-display font-bold text-white">Purple Partnerships</span></Link>
        <nav className="hidden md:flex gap-1">{tabs.map(t => (
          <button key={t.id} data-testid={`nav-${t.id}-tab`} onClick={() => setActive(t.id)} className={`px-4 py-2 rounded-full text-sm font-medium transition flex items-center gap-2 ${active === t.id ? "bg-[#FF1493] text-white" : "text-white/80 hover:bg-white/10"}`}>{t.icon}{t.label}</button>
        ))}</nav>
        <div className="flex items-center gap-2">
          {user?.role === "admin" && <Link to="/admin"><Button variant="ghost" size="sm" className="text-[#00FFFF] hover:bg-white/10" data-testid="admin-link"><Shield size={14} className="mr-1" />Admin</Button></Link>}
          <Button variant="ghost" size="sm" onClick={logout} className="text-white hover:bg-white/10" data-testid="logout-btn"><LogOut size={14} className="mr-1" />Logout</Button>
        </div>
      </div>
      <div className="md:hidden max-w-7xl mx-auto px-2 pb-2 flex gap-1 overflow-x-auto">{tabs.map(t => (
        <button key={t.id} data-testid={`nav-mobile-${t.id}`} onClick={() => setActive(t.id)} className={`px-3 py-2 rounded-full text-xs whitespace-nowrap ${active === t.id ? "bg-[#FF1493] text-white" : "text-white/80 bg-white/5"}`}>{t.label}</button>
      ))}</div>
    </header>
  );
}

function ProfileTab() {
  const { user, refresh } = useAuth();
  const [me, setMe] = useState(user);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/profile/me").then(r => setMe(r.data)); }, []);
  if (!me) return null;

  const setField = (k, v) => setMe(p => ({ ...p, [k]: v }));
  const setMandatory = (id, a) => setMe(p => {
    const arr = MANDATORY_PROMPTS.map(mp => {
      const existing = (p.mandatory_prompts || []).find(x => x.id === mp.id);
      const ans = mp.id === id ? a : existing?.a || "";
      return { id: mp.id, q: mp.q, a: ans };
    });
    return { ...p, mandatory_prompts: arr };
  });
  const setOptional = (idx, q, a) => setMe(p => {
    const arr = [...(p.optional_prompts || [])];
    arr[idx] = { id: `opt_${idx}`, q, a };
    return { ...p, optional_prompts: arr };
  });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/profile/me", {
        name: me.name, location: me.location, relationship_types: me.relationship_types,
        mandatory_prompts: me.mandatory_prompts, optional_prompts: me.optional_prompts,
      });
      toast.success("Profile saved. You're locked in.");
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); }
    finally { setSaving(false); }
  };

  const uploadPhoto = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/profile/photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo uploaded — clean & clear.");
      const r = await api.get("/profile/me"); setMe(r.data);
      return data;
    } catch (e) { toast.error(e?.response?.data?.detail || "Photo rejected."); }
  };

  const deletePhoto = async (id) => { await api.delete(`/profile/photos/${id}`); const r = await api.get("/profile/me"); setMe(r.data); };
  const makePrimary = async (id) => { await api.post(`/profile/photos/${id}/primary`); const r = await api.get("/profile/me"); setMe(r.data); toast.success("New profile pic locked in."); };

  const primaryPhoto = me.photos?.find(p => p.id === me.primary_photo_id) || me.photos?.[0];

  return (
    <div className="space-y-6">
      <div className="glass rounded-2xl p-6 flex flex-col sm:flex-row gap-6 items-start" data-testid="profile-header">
        <div className={`relative ${frameClass(me.tier)} rounded-full`}>
          <img src={primaryPhoto?.data_url || "https://api.dicebear.com/7.x/initials/svg?seed=" + encodeURIComponent(me.name || "PP")} className="w-32 h-32 rounded-full object-cover" alt="" />
          <span className="absolute -bottom-1 -right-1 bg-black/80 text-white text-[10px] uppercase font-bold rounded-full px-2 py-0.5 border border-white/30">{me.tier}</span>
        </div>
        <div className="flex-1">
          <h2 className="font-display text-3xl text-white font-bold">{me.name}, {me.age}</h2>
          <p className="text-white/70">{me.location} · {me.gender_identity} · {me.sexual_orientation}</p>
          {me.tier === "gold" && <Link to="/upgrade"><Button data-testid="upgrade-cta" size="sm" className="mt-3 bg-[#FF1493] hover:bg-[#d1107a] rounded-full">Upgrade to unlock messages</Button></Link>}
          {me.tier !== "gold" && !me.id_verified && <p className="text-yellow-300 text-sm mt-2">ID verification pending — admin will email you at {me.email}.</p>}
        </div>
      </div>

      <div className="glass rounded-2xl p-6">
        <h3 className="font-display text-xl text-white font-semibold mb-4">Your photos (up to 6, 8MP+)</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {(me.photos || []).map(p => (
            <div key={p.id} className={`relative aspect-square rounded-xl overflow-hidden border-2 ${p.id === me.primary_photo_id ? "border-[#FF1493]" : "border-white/15"}`} data-testid={`photo-${p.id}`}>
              <img src={p.data_url} className="w-full h-full object-cover" alt="" />
              <div className="absolute inset-0 bg-black/0 hover:bg-black/50 transition flex items-center justify-center gap-2 opacity-0 hover:opacity-100">
                {p.id !== me.primary_photo_id && <Button size="sm" data-testid={`primary-${p.id}`} onClick={() => makePrimary(p.id)} className="bg-[#FF1493] hover:bg-[#d1107a]">Make primary</Button>}
                <Button size="sm" variant="destructive" data-testid={`delete-${p.id}`} onClick={() => deletePhoto(p.id)}><X size={14} /></Button>
              </div>
              {p.id === me.primary_photo_id && <span className="absolute top-2 left-2 bg-[#FF1493] text-white text-[10px] px-2 py-0.5 rounded-full">PRIMARY</span>}
            </div>
          ))}
          {(me.photos || []).length < 6 && (
            <label className="aspect-square rounded-xl border-2 border-dashed border-white/30 flex flex-col items-center justify-center text-white/80 cursor-pointer hover:bg-white/5" data-testid="photo-upload">
              <Upload size={28} />
              <span className="text-sm mt-2">Add photo</span>
              <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadPhoto(e.target.files[0])} />
            </label>
          )}
        </div>
        <p className="text-white/60 text-xs mt-3">Photos are AI-moderated. No nudity, no group shots, no blurry pics. 8MP minimum.</p>
      </div>

      <div className="glass rounded-2xl p-6 space-y-4">
        <h3 className="font-display text-xl text-white font-semibold">Basics</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label className="text-white">Name</Label><Input data-testid="profile-name" value={me.name || ""} onChange={e => setField("name", e.target.value)} className="bg-white/10 text-white border-white/20" /></div>
          <div><Label className="text-white">Location</Label><Input data-testid="profile-location" value={me.location || ""} onChange={e => setField("location", e.target.value)} className="bg-white/10 text-white border-white/20" /></div>
          <div className="sm:col-span-2">
            <Label className="text-white">Relationship interest</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {["friendship", "dating", "marriage"].map(rt => (
                <button key={rt} type="button" data-testid={`rel-${rt}`} onClick={() => setField("relationship_types", (me.relationship_types||[]).includes(rt) ? me.relationship_types.filter(x => x !== rt) : [...(me.relationship_types||[]), rt])}
                  className={`px-3 py-1.5 rounded-full text-sm border capitalize ${ (me.relationship_types||[]).includes(rt) ? "bg-[#FF1493] border-[#FF1493] text-white" : "border-white/30 text-white/80"}`}>{rt}</button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="glass rounded-2xl p-6 space-y-4">
        <h3 className="font-display text-xl text-white font-semibold">Real Talk (mandatory)</h3>
        {MANDATORY_PROMPTS.map(mp => {
          const v = (me.mandatory_prompts||[]).find(x => x.id === mp.id)?.a || "";
          return (
            <div key={mp.id}>
              <Label className="text-white">{mp.q}</Label>
              <Textarea data-testid={`mand-${mp.id}`} value={v} maxLength={mp.maxLen} onChange={(e) => setMandatory(mp.id, e.target.value)} className="bg-white/10 text-white border-white/20" rows={2} />
              <p className="text-white/50 text-xs mt-1">{v.length}/{mp.maxLen}</p>
            </div>
          );
        })}
      </div>

      <div className="glass rounded-2xl p-6 space-y-4">
        <h3 className="font-display text-xl text-white font-semibold">Say Less (optional — up to 6)</h3>
        {[0,1,2,3,4,5].map(idx => {
          const cur = me.optional_prompts?.[idx];
          return (
            <div key={idx} className="space-y-2">
              <Select value={cur?.q || ""} onValueChange={(v) => setOptional(idx, v, cur?.a || "")}>
                <SelectTrigger data-testid={`opt-prompt-${idx}`} className="bg-white/10 text-white border-white/20"><SelectValue placeholder={`Choose prompt #${idx+1}`} /></SelectTrigger>
                <SelectContent className="max-h-80">{OPTIONAL_PROMPTS.map(q => <SelectItem key={q} value={q}>{q}</SelectItem>)}</SelectContent>
              </Select>
              {cur?.q && <Textarea data-testid={`opt-answer-${idx}`} placeholder="Your answer..." value={cur?.a || ""} onChange={(e) => setOptional(idx, cur.q, e.target.value)} className="bg-white/10 text-white border-white/20" rows={2} />}
            </div>
          );
        })}
        {me.tier !== "gold" && <p className="text-[#00FFFF] text-sm">🎙 Voice notes for prompts coming soon for Platinum & Diamond members.</p>}
      </div>

      <Button data-testid="profile-save" onClick={save} disabled={saving} className="bg-[#FF1493] hover:bg-[#d1107a] rounded-full px-8 py-6 font-bold">{saving ? "Saving..." : "Save profile"}</Button>
    </div>
  );
}

function ProfileCard({ p, onFavorite, onReport, favorited, isPlatinumPlus }) {
  return (
    <div className="glass rounded-2xl p-5 fade-up" data-testid={`profile-card-${p.id}`}>
      <div className="flex items-start gap-4">
        <div className={`shrink-0 ${frameClass(p.tier)} rounded-full`}>
          <img src={p.primary_photo || "https://api.dicebear.com/7.x/initials/svg?seed=" + encodeURIComponent(p.name||"PP")} className="w-20 h-20 rounded-full object-cover" alt="" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-display text-xl text-white font-bold truncate">{p.name}, {p.age}</h3>
            {p.id_verified && <span className="text-[#00FFFF] text-xs font-bold">✓ ID</span>}
            <span className="ml-auto text-[10px] uppercase font-bold text-white/60">{p.tier}</span>
          </div>
          <p className="text-white/70 text-sm">{p.location}</p>
          <p className="text-white/70 text-xs mt-0.5">{p.gender_identity} · {p.sexual_orientation}</p>
        </div>
      </div>
      {p.mandatory_prompts?.[5]?.a && <p className="text-white/85 mt-3 text-sm line-clamp-3">"{p.mandatory_prompts[5].a}"</p>}
      <div className="flex gap-2 mt-4">
        <Button size="sm" data-testid={`fav-${p.id}`} onClick={() => onFavorite(p)} variant="outline" className={`flex-1 border-white/30 ${favorited ? "bg-[#FF1493] text-white border-[#FF1493]" : "text-white bg-transparent hover:bg-white/10"}`}><Heart size={14} className="mr-1" fill={favorited ? "currentColor" : "none"} />{favorited ? "Saved" : "Save"}</Button>
        {isPlatinumPlus && <Button size="sm" className="flex-1 bg-[#FF1493] hover:bg-[#d1107a]" data-testid={`msg-${p.id}`} onClick={() => onReport(p, "_msg")}>Message</Button>}
        <Button size="sm" variant="ghost" data-testid={`report-${p.id}`} onClick={() => onReport(p, "_report")} className="text-white/80 hover:bg-white/10"><Flag size={14} /></Button>
      </div>
    </div>
  );
}

function SearchTab() {
  const { user } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [favorites, setFavorites] = useState(new Set());
  const [q, setQ] = useState("");
  const [location, setLocation] = useState("");
  const [reportTarget, setReportTarget] = useState(null);
  const [reportReason, setReportReason] = useState("");
  const [reportNotes, setReportNotes] = useState("");
  const [msgTarget, setMsgTarget] = useState(null);
  const [msgBody, setMsgBody] = useState("");
  const isPlatinumPlus = user?.tier === "platinum" || user?.tier === "diamond";

  const load = useCallback(async () => {
    const { data } = await api.get("/profiles", { params: { q: q || undefined, location: location || undefined } });
    setProfiles(data);
    const fav = await api.get("/favorites");
    setFavorites(new Set(fav.data.map(x => x.id)));
  }, [q, location]);
  useEffect(() => { load(); }, [load]);

  const toggleFav = async (p) => {
    if (favorites.has(p.id)) { await api.delete(`/favorites/${p.id}`); setFavorites(s => { const n = new Set(s); n.delete(p.id); return n; }); toast("Removed from favorites"); }
    else { await api.post(`/favorites/${p.id}`); setFavorites(s => new Set(s).add(p.id)); toast.success("Locked in to your top tier"); }
  };
  const openReport = (p, kind) => { if (kind === "_msg") { setMsgTarget(p); } else { setReportTarget(p); } };

  const submitReport = async () => {
    if (!reportReason) return toast.error("Pick a reason.");
    await api.post("/reports", { reported_user_id: reportTarget.id, reason: reportReason, notes: reportNotes });
    toast.success("Report sent. Our team will handle it ASAP.");
    setReportTarget(null); setReportReason(""); setReportNotes("");
  };
  const submitMsg = async () => {
    try { await api.post("/messages", { recipient_id: msgTarget.id, body: msgBody }); toast.success("Message sent."); setMsgTarget(null); setMsgBody(""); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send."); }
  };

  return (
    <div>
      <div className="glass rounded-2xl p-5 mb-6 flex flex-wrap gap-3 items-end" data-testid="search-filters">
        <div className="flex-1 min-w-[200px]"><Label className="text-white text-xs">Name</Label><Input data-testid="search-q" value={q} onChange={e => setQ(e.target.value)} placeholder="Search by name..." className="bg-white/10 text-white border-white/20" /></div>
        <div className="flex-1 min-w-[200px]"><Label className="text-white text-xs">Location</Label><Input data-testid="search-location" value={location} onChange={e => setLocation(e.target.value)} placeholder="City..." className="bg-white/10 text-white border-white/20" /></div>
        <Button data-testid="search-go" onClick={load} className="bg-[#FF1493] hover:bg-[#d1107a] rounded-full">Find your vibe</Button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {profiles.length === 0 && <p className="text-white/70 col-span-full text-center py-12">No profiles match yet. Tweak your filters.</p>}
        {profiles.map(p => <ProfileCard key={p.id} p={p} favorited={favorites.has(p.id)} onFavorite={toggleFav} onReport={openReport} isPlatinumPlus={isPlatinumPlus} />)}
      </div>

      <Dialog open={!!reportTarget} onOpenChange={(o) => !o && setReportTarget(null)}>
        <DialogContent className="glass border-white/15 text-white" data-testid="report-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Check them — Report profile</DialogTitle></DialogHeader>
          <p className="text-white/80 text-sm">Reporting {reportTarget?.name}. Tell us what's up.</p>
          <Select value={reportReason} onValueChange={setReportReason}>
            <SelectTrigger data-testid="report-reason" className="bg-white/10 border-white/20 text-white"><SelectValue placeholder="Pick a reason..." /></SelectTrigger>
            <SelectContent>{REPORT_REASONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
          </Select>
          <Textarea data-testid="report-notes" value={reportNotes} onChange={(e) => setReportNotes(e.target.value)} placeholder="Add any details (optional)..." className="bg-white/10 border-white/20 text-white" />
          <Button data-testid="report-submit" onClick={submitReport} className="bg-[#FF1493] hover:bg-[#d1107a]">Send report</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!msgTarget} onOpenChange={(o) => !o && setMsgTarget(null)}>
        <DialogContent className="glass border-white/15 text-white" data-testid="message-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Slide into the DMs</DialogTitle></DialogHeader>
          <p className="text-white/80 text-sm">Encrypted message to {msgTarget?.name}.</p>
          <Textarea data-testid="message-body" value={msgBody} onChange={(e) => setMsgBody(e.target.value)} placeholder="Say hi with respect..." className="bg-white/10 border-white/20 text-white" rows={4} />
          <Button data-testid="message-submit" onClick={submitMsg} className="bg-[#FF1493] hover:bg-[#d1107a]">Send</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FavoritesTab() {
  const [list, setList] = useState([]);
  const load = useCallback(async () => { const { data } = await api.get("/favorites"); setList(data); }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div data-testid="favorites-grid">
      <h2 className="font-display text-2xl text-white font-bold mb-4">Top Tier — your locked-in profiles</h2>
      {list.length === 0 ? <p className="text-white/70">No favorites yet. Tap the heart on a profile to save it.</p> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">{list.map(p => (
          <div key={p.id} className="glass rounded-2xl p-5">
            <div className="flex gap-4">
              <div className={`shrink-0 ${frameClass(p.tier)} rounded-full`}><img src={p.primary_photo || "https://api.dicebear.com/7.x/initials/svg?seed=" + encodeURIComponent(p.name)} className="w-16 h-16 rounded-full object-cover" alt="" /></div>
              <div><h3 className="font-display text-lg text-white font-bold">{p.name}, {p.age}</h3><p className="text-white/70 text-sm">{p.location}</p></div>
            </div>
          </div>
        ))}</div>
      )}
    </div>
  );
}

function InboxTab() {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  useEffect(() => {
    let cancelled = false;
    api.get("/messages/threads")
      .then(r => { if (!cancelled) setThreads(r.data); })
      .catch(err => console.error("Inbox load failed:", err));
    return () => { cancelled = true; };
  }, []);
  if (user?.tier === "gold") {
    return <div className="glass rounded-2xl p-8 text-center" data-testid="inbox-locked"><h2 className="font-display text-2xl text-white font-bold">Inbox is locked</h2><p className="text-white/80 mt-2">Encrypted messages unlock at Platinum.</p><Link to="/upgrade"><Button className="mt-4 bg-[#FF1493] hover:bg-[#d1107a] rounded-full">Secure the bag</Button></Link></div>;
  }
  return (
    <div data-testid="inbox">
      <h2 className="font-display text-2xl text-white font-bold mb-4">What's good — your inbox</h2>
      {threads.length === 0 ? <p className="text-white/70">No DMs yet. Start a conversation from the Search tab.</p> : (
        <div className="space-y-3">{threads.map((t) => (
          <div key={t.with.id} className="glass rounded-2xl p-5">
            <p className="font-display text-lg text-white font-bold">{t.with.name}</p>
            <div className="mt-2 space-y-1">{t.messages.slice(0,3).map((m) => <p key={`${m.ts}-${m.from}`} className="text-white/85 text-sm">↳ {m.body}</p>)}</div>
          </div>
        ))}</div>
      )}
    </div>
  );
}

function HelpTab() {
  return (
    <div className="space-y-4" data-testid="help-tab">
      <h2 className="font-display text-2xl text-white font-bold">Help & Safety</h2>
      <div className="glass rounded-2xl p-6">
        <h3 className="font-display text-lg text-white font-semibold mb-2">Need to report someone?</h3>
        <p className="text-white/80">Tap the flag icon on any profile. Our team reviews ASAP.</p>
      </div>
      <div className="glass rounded-2xl p-6">
        <h3 className="font-display text-lg text-white font-semibold mb-2">Crisis support</h3>
        <p className="text-white/80">LGBTQ+ Crisis Hotline: <strong>988 (press 3)</strong>. Trevor Project: 1-866-488-7386.</p>
      </div>
      <div className="glass rounded-2xl p-6">
        <h3 className="font-display text-lg text-white font-semibold mb-2">Legal</h3>
        <div className="flex flex-wrap gap-3 text-[#00FFFF]">
          <Link to="/legal/terms" className="underline">Terms of Service</Link>
          <Link to="/legal/privacy" className="underline">Privacy Policy</Link>
          <Link to="/legal/disclaimer" className="underline">Disclaimer</Link>
          <Link to="/legal/guidelines" className="underline">Community Guidelines</Link>
        </div>
      </div>
      <div className="glass rounded-2xl p-6">
        <h3 className="font-display text-lg text-white font-semibold mb-2">Contact support</h3>
        <p className="text-white/80">Email <a className="neon-pink-text" href="mailto:purplepartnerships@proton.me">purplepartnerships@proton.me</a>.</p>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [active, setActive] = useState("search");
  return (
    <div className="min-h-screen" style={{ background: "var(--lavender)" }}>
      <TopNav active={active} setActive={setActive} />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {active === "search" && <SearchTab />}
        {active === "profile" && <ProfileTab />}
        {active === "favorites" && <FavoritesTab />}
        {active === "inbox" && <InboxTab />}
        {active === "help" && <HelpTab />}
      </main>
    </div>
  );
}
